import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@staff-app/ui';
import { ADMIN_ROLES, type AdminRole, type AdminUser } from '@staff-app/shared-types';
import { STAFF_UPDATE_ADMIN_USER_ROLE } from '@staff-app/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';

interface Props {
  user: AdminUser | null;
  onOpenChange: (open: boolean) => void;
}

interface UpdateResult {
  staffUpdateAdminUserRole: AdminUser;
}

export function EditRoleDialog({ user, onOpenChange }: Props) {
  const me = useAuthStore((s) => s.adminUser);
  const [role, setRole] = useState<AdminRole>(user?.role ?? ADMIN_ROLES.ENGINEER);

  useEffect(() => {
    if (user) setRole(user.role);
  }, [user]);

  const [updateMutation, { loading }] = useMutation<UpdateResult>(STAFF_UPDATE_ADMIN_USER_ROLE, {
    refetchQueries: ['staffAdminUsers'],
  });

  // Block self-demote from root — UX-level enforcement; backend should reject too.
  const isSelfDemoteFromRoot =
    !!user && !!me && user.email === me.email && user.role === 'root' && role !== 'root';

  const { disabled: submitDisabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: loading,
    isDirty: !!user && role !== user.role,
    validationError: isSelfDemoteFromRoot ? 'You cannot demote yourself from root.' : '',
  });

  const handleSubmit = async () => {
    if (!user) return;
    try {
      await updateMutation({ variables: { email: user.email, role: role.toUpperCase() as AdminRole } });
      toast.success('Role updated');
      onOpenChange(false);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Unknown error';
      toast.error('Role update failed', { description: msg });
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-role-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{'Change role'}</DialogTitle>
        </DialogHeader>

        {user ? (
          <div className="flex flex-col gap-4 py-2">
            <div className="rounded-md bg-muted px-3 py-2 text-sm font-medium">{user.email}</div>
            <div className="space-y-2">
              <Label>{'Role'}</Label>
              <RadioGroup
                value={role}
                onValueChange={(v) => setRole(v as AdminRole)}
                className="grid grid-cols-2 gap-2"
              >
                <RoleOption value={ADMIN_ROLES.ROOT} label={'Root'} />
                <RoleOption value={ADMIN_ROLES.ENGINEER} label={'Engineer'} />
                <RoleOption value={ADMIN_ROLES.SUPPORT} label={'Support'} />
                <RoleOption value={ADMIN_ROLES.READONLY} label={'Read only'} />
              </RadioGroup>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{'Cancel'}</Button>
          </DialogClose>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={submitDisabled ? 0 : -1}>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    data-testid="edit-role-submit"
                  >
                    {loading ? 'Saving…' : 'Save'}
                  </Button>
                </span>
              </TooltipTrigger>
              {disabledTooltip ? <TooltipContent>{disabledTooltip}</TooltipContent> : null}
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleOption({ value, label }: { value: AdminRole; label: string }) {
  const id = `edit-role-${value}`;
  return (
    <div className="flex items-center gap-2 rounded-md border px-3 py-2">
      <RadioGroupItem id={id} value={value} />
      <Label htmlFor={id} className="cursor-pointer">
        {label}
      </Label>
    </div>
  );
}
